import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { AuthState } from '../../../auth/store/auth.model';
import { selectUser } from '../../../auth/store/auth.selector';
import { ProfileApiService } from '../../../pages/profile/profile-api.service';
import { ToasterService } from '../../../providers/toaster/toaster.service';

@Component({
  selector: 'app-address-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './address-details.component.html',
  styleUrl: './address-details.component.css'
})
export class AddressDetailsComponent implements OnInit {
  openSection: string | null = null;
  addressForm: FormGroup;
  userId: number | string | null = null;
  isLoading = false;
  isSaving = false;

  constructor(
    private fb: FormBuilder,
    private store: Store<AuthState>,
    private profileApi: ProfileApiService,
    private toasterService: ToasterService
  ) {
    this.addressForm = this.fb.group({
      current: this.fb.group({
        address: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(255), this.noLeadingTrailingSpaces()]],
        city: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100), this.noLeadingTrailingSpaces()]],
        district: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100), this.noLeadingTrailingSpaces()]],
        state: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100), this.noLeadingTrailingSpaces()]],
        pincode: ['', [Validators.required, Validators.pattern('^[0-9]{6}$')]],
        // country: ['', Validators.required],
      }),
      permanent: this.fb.group({
        address: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(255), this.noLeadingTrailingSpaces()]],
        city: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100), this.noLeadingTrailingSpaces()]],
        district: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100), this.noLeadingTrailingSpaces()]],
        state: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100), this.noLeadingTrailingSpaces()]],
        pincode: ['', [Validators.required, Validators.pattern('^[0-9]{6}$')]],
        // country: ['', Validators.required],
      })
    });
  }

  ngOnInit() {
  this.store.select(selectUser).pipe(take(1)).subscribe({
    next: (user) => {
      const masterUserId =
        localStorage.getItem('master_user_id') ??
        user?.id ??
        this.getUserIdFromStorage();

      if (masterUserId) {
        this.userId = masterUserId; // assign here
        this.loadAddress(masterUserId);
      }
    }
  });
}

  get currentAddress(): FormGroup {
    return this.addressForm.get('current') as FormGroup;
  }

  get permanentAddress(): FormGroup {
    return this.addressForm.get('permanent') as FormGroup;
  }

  toggleSection(section: string, event: Event) {
    event.preventDefault();
    this.openSection = this.openSection === section ? null : section;
  }

  copyCurrentToPermanent(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.permanentAddress.patchValue(this.currentAddress.value);
    this.openSection = 'permanent';
  }

  private loadAddress(userId: number | string) {
    this.isLoading = true;
    this.profileApi.getUserAddressById(userId).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const data = res?.data ?? res ?? {};
        const current = data.current || {};
        const permanent = data.permanent || {};
        this.addressForm.patchValue({
          current: {
            address: current.address || '',
            city: current.city || '',
            district: current.district || '',
            state: current.state || '',
            pincode: current.pincode || '',
            // country: current.country || ''
          },
          permanent: {
            address: permanent.address || '',
            city: permanent.city || '',
            district: permanent.district || '',
            state: permanent.state || '',
            pincode: permanent.pincode || '',
            // country: permanent.country || ''
          }
        });
      },
      error: () => {
        this.isLoading = false;
        this.toasterService.setToaster('error', 'Error', 'Unable to load address details');
      }
    });
  }

  saveAddress() {
    if (this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      return;
    }

    if (!this.userId) {
      this.toasterService.setToaster('error', 'Error', 'User ID not found.');
      return;
    }

    const payload = [
      {
        user_id: this.userId  ,
        address_type: 'current',
        ...this.currentAddress.value,
        pincode: this.currentAddress.value.pincode?.toString() || ''
      },
      {
        user_id: this.userId  ,
        address_type: 'permanent',
        ...this.permanentAddress.value,
        pincode: this.permanentAddress.value.pincode?.toString() || ''
      }
    ];

    this.isSaving = true;
    this.profileApi.saveUserAddress(payload).subscribe({
      next: (res: any) => {
        this.isSaving = false;
        if (res?.status === 'success' || res?.status === 200) {
          this.toasterService.setToaster('success', 'Success', res.message ?? 'Address details saved successfully');
        } else {
          this.toasterService.setToaster('error', 'Error', res?.message ?? 'Failed to save address details');
        }
      },
      error: (err: any) => {
        this.isSaving = false;
        this.toasterService.setToaster('error', 'Failed', err?.message || 'Unable to save address details');
      }
    });
  }

  noLeadingTrailingSpaces() {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (typeof value === 'string' && value.length > 0) {
        const hasLeadingSpace = value.charAt(0) === ' ';
        const hasTrailingSpace = value.charAt(value.length - 1) === ' ';
        if (hasLeadingSpace || hasTrailingSpace) {
          return { leadingTrailingSpaces: true };
        }
      }
      return null;
    };
  }

  getFieldError(control: AbstractControl | null, fieldName: string): string {
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    const label = this.getAddressFieldLabel(fieldName);
    const errors = control.errors;

    if (errors['required']) {
      return `${label} is required.`;
    }
    if (errors['minlength']) {
      return `${label} must be between ${errors['minlength'].requiredLength} and ${errors['maxlength']?.requiredLength || ''} characters.`;
    }
    if (errors['maxlength']) {
      return `${label} must not exceed ${errors['maxlength'].requiredLength} characters.`;
    }
    if (errors['leadingTrailingSpaces']) {
      return `${label} must not have leading or trailing spaces.`;
    }
    if (errors['pattern']) {
      if (fieldName.includes('pincode')) {
        return 'Pincode must be exactly 6 digits.';
      }
      return `Please enter a valid ${label.toLowerCase()}.`;
    }

    return '';
  }

  private getAddressFieldLabel(fieldName: string): string {
    const labels: Record<string, string> = {
      'current.address': 'Address',
      'current.city': 'City',
      'current.district': 'District',
      'current.state': 'State',
      'current.pincode': 'Pincode',
      // 'current.country': 'Current country',
      'permanent.address': 'Address',
      'permanent.city': 'City',
      'permanent.district': 'District',
      'permanent.state': 'State',
      'permanent.pincode': 'Pincode',
      // 'permanent.country': 'Permanent country'
    };
    return labels[fieldName] || fieldName;
  }

  private getUserIdFromStorage(): number | null {
    const authData = localStorage.getItem('auth_data');
    if (!authData) {
      return null;
    }
    try {
      const parsed = JSON.parse(authData);
      return parsed?.user?.id ?? null;
    } catch {
      return null;
    }
  }
}
